import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { CarouselImage } from '../types/content';
interface ImageLightboxProps {
  images: CarouselImage[];
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
}
export function ImageLightbox({
  images,
  index,
  onClose,
  onNavigate
}: ImageLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = index !== null;
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (index === null) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNavigate((index + 1) % images.length);
      if (e.key === 'ArrowLeft') onNavigate((index - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, index, images.length, onClose, onNavigate]);
  const image = index !== null ? images[index] : null;
  return <AnimatePresence data-id="element-196" data-mp="1kbspwithut75">
      {image && index !== null && <motion.div role="dialog" aria-modal="true" aria-label={`Enlarged page ${index + 1} of ${images.length}: ${image.caption}`} className="fixed inset-0 z-50 bg-navy-dark/90 flex items-center justify-center p-4 sm:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} onClick={onClose} data-id="element-197" data-mp="1pd88m4878nmd">
          <motion.img key={image.src} src={image.src} alt={image.alt} className="max-h-[88vh] max-w-full w-auto rounded-xl shadow-2xl bg-white" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={e => e.stopPropagation()} data-id="element-198" data-mp="rooosoxiuz0n" />
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close enlarged image" className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white text-navy flex items-center justify-center hover:bg-seafoam-light transition-colors duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-seafoam" data-id="element-199" data-mp="3uzxk3j16l8gs">
            <X size={22} data-id="element-200" data-mp="3c11s8ecsp664" />
          </button>
          <button type="button" onClick={e => {
        e.stopPropagation();
        onNavigate((index - 1 + images.length) % images.length);
      }} aria-label="Previous image" className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 text-navy flex items-center justify-center hover:bg-white transition-colors duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-seafoam" data-id="element-201" data-mp="3c2d6j83v9wbh">
            <ChevronLeft size={22} data-id="element-202" data-mp="1rjj6810jplwt" />
          </button>
          <button type="button" onClick={e => {
        e.stopPropagation();
        onNavigate((index + 1) % images.length);
      }} aria-label="Next image" className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 text-navy flex items-center justify-center hover:bg-white transition-colors duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-seafoam" data-id="element-203" data-mp="1whuc60npzc9r">
            <ChevronRight size={22} data-id="element-204" data-mp="2k7ygka8qfg1t" />
          </button>
          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm font-medium" data-id="element-205" data-mp="2k7eymtj30rns">
            {index + 1} / {images.length}
          </p>
        </motion.div>}
    </AnimatePresence>;
}